<template>
    <section class="carousel" :aria-label="sectionLabel" :aria-roledescription="t('components.carousel.roleDescription')">
        <div class="viewport" :class="{ 'is-sliding': animated, 'is-fitted': fitActive, 'is-multiple': visibleCount > 1 }">
            <div class="track" :style="{ '--visible-slides': visibleCount, '--slide-offset': currentIndex }">
                <div
                    v-for="(id, index) in itemIds"
                    :key="id"
                    :ref="(element) => setSlideElement(id, element)"
                    class="slide"
                    :class="{ 'is-before': index < currentIndex, 'is-after': index > currentIndex }"
                    role="group"
                    tabindex="-1"
                    :aria-roledescription="t('components.carousel.slideRoleDescription')"
                    :aria-label="t('components.carousel.position', { index: index + 1, total: itemIds.length })"
                    :aria-hidden="!isVisible(index)"
                    :inert="!isVisible(index)">
                    <slot :name="`item-${id}`"></slot>
                </div>
            </div>
        </div>
        <div v-if="!hideControls && maxIndex > 0" ref="controls" class="controls">
            <BaseButton
                theme="tertiary"
                size="sm"
                icon-only
                data-test-id="carousel-prev"
                class="control"
                :disabled="currentIndex === 0"
                :aria-label="t('components.carousel.previous', { itemLabel: resolvedItemLabel })"
                @click="move(-1)"
                @keydown="handleControlKey">
                <i class="bi bi-arrow-left" aria-hidden="true"></i>
            </BaseButton>
            <div class="progress">
                <template v-if="showCount">
                    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -- Naming this status adds a repeated prefix to the live count (PLT-2600). -->
                    <output class="count">
                        <slot name="count" :start="currentIndex + 1" :end="currentIndex + visibleCount" :total="itemIds.length">{{
                            t("components.carousel.position", { index: countPosition, total: itemIds.length })
                        }}</slot>
                    </output>
                </template>
                <div class="dots">
                    <BaseButton
                        v-for="(id, index) in positions"
                        :key="id"
                        theme="tertiary"
                        size="sm"
                        icon-only
                        class="dot-button"
                        :aria-label="t('components.carousel.goTo', { itemLabel: resolvedItemLabel, index: index + 1 })"
                        :aria-current="activeId === id ? 'true' : undefined"
                        @click="activeId = id"
                        @keydown="handleControlKey">
                        <span class="dot" :class="{ 'is-active': activeId === id }" aria-hidden="true"></span>
                    </BaseButton>
                </div>
            </div>
            <BaseButton
                theme="tertiary"
                size="sm"
                icon-only
                data-test-id="carousel-next"
                class="control"
                :disabled="currentIndex === maxIndex"
                :aria-label="t('components.carousel.next', { itemLabel: resolvedItemLabel })"
                @click="move(1)"
                @keydown="handleControlKey">
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { invariant } from "~vue/utils";
import { type ComponentPublicInstance, computed, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const {
    itemIds,
    hideControls = false,
    animated = false,
    showCount = false,
    fitActive = false,
    itemsPerView = 1,
    label,
    itemLabel,
} = defineProps<{
    itemIds: string[];
    /** Number of visible items; navigation advances one item at a time. */
    itemsPerView?: number;
    hideControls?: boolean;
    animated?: boolean;
    showCount?: boolean;
    /**
     * Fits the active slide's height. Disabled by default to prevent content below the carousel from
     * shifting; enable to avoid excess space beneath shorter slides.
     */
    fitActive?: boolean;
    label?: string;
    itemLabel?: string;
}>();

const sectionLabel = computed(() => label ?? t("components.carousel.label"));
const resolvedItemLabel = computed(() => itemLabel ?? t("components.carousel.itemLabel"));

const slideElements = new Map<string, HTMLElement>();
const controls = useTemplateRef<HTMLElement>("controls");
const activeId = defineModel<string>({ default: "" });
const visibleCount = computed(() => Math.min(itemIds.length, Math.max(1, Math.floor(itemsPerView))));
const maxIndex = computed(() => Math.max(0, itemIds.length - visibleCount.value));
const positions = computed(() => itemIds.slice(0, maxIndex.value + 1));
const currentIndex = computed(() => itemIds.indexOf(activeId.value));
const countPosition = computed(() => (visibleCount.value === 1 ? currentIndex.value + 1 : `${currentIndex.value + 1}–${currentIndex.value + visibleCount.value}`));
watch(
    [() => itemIds, visibleCount, () => hideControls],
    ([ids]) => {
        invariant(ids.length > 0, "BaseCarousel requires at least one itemId");
        if (!ids.includes(activeId.value)) {
            activeId.value = ids[0];
        } else if (ids.indexOf(activeId.value) > maxIndex.value) {
            activeId.value = ids[maxIndex.value];
        }
        const focusedIndex = ids.findIndex((id) => slideElements.get(id)?.contains(document.activeElement));
        if (focusedIndex >= 0 && !isVisible(focusedIndex)) {
            activeId.value = ids[Math.min(focusedIndex, maxIndex.value)];
        }
        if ((hideControls || maxIndex.value === 0) && controls.value?.contains(document.activeElement)) {
            const survivingId = ids.find((id, index) => isVisible(index) && slideElements.get(id)?.getAttribute("aria-hidden") === "false");
            if (survivingId) {
                slideElements.get(survivingId)?.focus();
            }
        }
    },
    { immediate: true },
);

function setSlideElement(id: string, element: Element | ComponentPublicInstance | null): void {
    if (element instanceof HTMLElement) {
        slideElements.set(id, element);
    } else {
        slideElements.delete(id);
    }
}

function isVisible(index: number): boolean {
    return index >= currentIndex.value && index < currentIndex.value + visibleCount.value;
}

function handleControlKey(event: KeyboardEvent): void {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        move(event.key === "ArrowLeft" ? -1 : 1);
    }
}

function move(delta: number): void {
    const next = currentIndex.value + delta;
    if (next >= 0 && next <= maxIndex.value) {
        activeId.value = itemIds[next];
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .carousel {
        @apply flex min-w-0 flex-col justify-between gap-4;
    }

    .viewport {
        @apply relative grid min-w-0;
    }

    .viewport.is-sliding {
        @apply overflow-hidden;
    }

    .track {
        display: contents;
    }

    .viewport.is-multiple {
        @apply overflow-hidden;
    }

    .is-multiple .track {
        @apply flex min-w-0 gap-3;
        transform: translateX(calc(var(--slide-offset) * -1 * (100% + 12px) / var(--visible-slides)));
    }

    .is-multiple .slide {
        flex: 0 0 calc((100% - (var(--visible-slides) - 1) * 12px) / var(--visible-slides));
    }

    .is-multiple.is-sliding .track {
        @apply transition-transform duration-200 ease-out motion-reduce:transition-none;
    }

    .is-multiple .slide[aria-hidden="true"] {
        @apply visible;
    }

    .slide {
        @apply col-start-1 row-start-1 min-w-0;
    }

    .slide[aria-hidden="true"] {
        @apply invisible pointer-events-none;
    }

    /*
     * Keep inactive slides positioned over the viewport so outgoing animations remain visible without
     * affecting the active slide's height.
     */
    .is-fitted:not(.is-multiple) .slide[aria-hidden="true"] {
        @apply absolute inset-0;
    }

    .is-sliding:not(.is-multiple) .slide {
        @apply transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none;
    }

    .is-sliding:not(.is-multiple) .slide[aria-hidden="true"] {
        @apply visible opacity-0;
    }

    .is-sliding:not(.is-multiple) .slide.is-before {
        @apply -translate-x-full;
    }

    .is-sliding:not(.is-multiple) .slide.is-after {
        @apply translate-x-full;
    }

    .controls {
        @apply flex w-full items-center justify-between gap-2;
    }

    .control {
        @apply flex size-8 items-center justify-center rounded-full bg-carousel-control-bg text-carousel-control-text transition-colors hover:bg-carousel-control-hover-bg disabled:bg-carousel-control-disabled-bg disabled:opacity-50;
    }

    .progress {
        @apply flex flex-col items-center;
    }

    .count {
        @apply scaffold-details-small text-ink-muted;
    }

    .dots {
        @apply flex;
    }

    .dot-button {
        @apply flex size-8 items-center justify-center rounded-full;
    }

    .dot {
        @apply size-2.5 rounded-full border border-carousel-dot-border;
    }

    .dot.is-active {
        @apply bg-carousel-dot-active-bg;
    }
}
</style>
